import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Loader } from "@/components/Common";
import AppLayout from "@/components/AppLayout";
import CookieConsent from "@/components/CookieConsent";

import Landing from "@/pages/public/Landing";
import Pricing from "@/pages/public/Pricing";
import { Login, Register } from "@/pages/public/AuthPages";
import RegisterChoice from "@/pages/public/RegisterChoice";
import { MentionsLegales, CGV, Confidentialite } from "@/pages/public/Legal";
import Onboarding from "@/pages/public/Onboarding";
import PublicRSVP from "@/pages/public/PublicRSVP";
import JoinInvite from "@/pages/public/JoinInvite";
import Marketplace from "@/pages/public/Marketplace";
import VendorProfile from "@/pages/public/VendorProfile";
import PaymentSuccess from "@/pages/public/PaymentSuccess";

import Dashboard from "@/pages/app/Dashboard";
import Guests from "@/pages/app/Guests";
import RsvpBuilder from "@/pages/app/RsvpBuilder";
import Seating from "@/pages/app/Seating";
import Budget from "@/pages/app/Budget";
import Tasks from "@/pages/app/Tasks";
import Vendors from "@/pages/app/Vendors";
import Messages from "@/pages/app/Messages";
import AiAssistant from "@/pages/app/AiAssistant";
import Settings from "@/pages/app/Settings";
import Invitations from "@/pages/app/Invitations";
import SiteBuilder from "@/pages/app/SiteBuilder";
import Gazette from "@/pages/app/Gazette";
import Premium from "@/pages/app/Premium";
import Support from "@/pages/app/Support";
import WeddingDay from "@/pages/app/WeddingDay";
import DayShare from "@/pages/public/DayShare";
import PublicSite from "@/pages/public/PublicSite";

import VendorPortal from "@/pages/vendor/VendorPortal";
import SuperAdmin from "@/pages/admin/SuperAdmin";

function Protected({ children, requireWedding = false }) {
  const { user, wedding } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (requireWedding && !user.wedding_id) {
    if (user.role === "VENDOR_OWNER" || user.vendor_id) return <Navigate to="/vendor" replace />;
    return <Navigate to="/onboarding" replace />;
  }
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  if (user === null) return <Loader />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.role !== "SUPER_ADMIN") return <Navigate to="/app" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/pricing" element={<Pricing />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<RegisterChoice />} />
      <Route path="/register/couple" element={<Register mode="couple" />} />
      <Route path="/register/vendor" element={<Register mode="vendor" />} />
      <Route path="/onboarding" element={<Protected><Onboarding /></Protected>} />
      <Route path="/rsvp/:token" element={<PublicRSVP />} />
      <Route path="/jour-j/:token" element={<DayShare />} />
      <Route path="/w/:slug" element={<PublicSite />} />
      <Route path="/join/:token" element={<JoinInvite />} />
      <Route path="/mentions-legales" element={<MentionsLegales />} />
      <Route path="/cgv" element={<CGV />} />
      <Route path="/confidentialite" element={<Confidentialite />} />
      <Route path="/marketplace" element={<Marketplace />} />
      <Route path="/marketplace/:slug" element={<VendorProfile />} />
      <Route path="/prestataires/:slug" element={<VendorProfile />} />
      <Route path="/payment/success" element={<Protected><PaymentSuccess /></Protected>} />

      <Route path="/app" element={<Protected requireWedding><AppLayout /></Protected>}>
        <Route index element={<Dashboard />} />
        <Route path="tasks" element={<Tasks />} />
        <Route path="guests" element={<Guests />} />
        <Route path="rsvp" element={<RsvpBuilder />} />
        <Route path="seating" element={<Seating />} />
        <Route path="budget" element={<Budget />} />
        <Route path="jour-j" element={<WeddingDay />} />
        <Route path="vendors" element={<Vendors />} />
        <Route path="messages" element={<Messages />} />
        <Route path="ai" element={<AiAssistant />} />
        <Route path="invitations" element={<Invitations />} />
        <Route path="website" element={<SiteBuilder />} />
        <Route path="gazette" element={<Gazette />} />
        <Route path="premium" element={<Premium />} />
        <Route path="support" element={<Support />} />
        <Route path="settings" element={<Settings />} />
      </Route>

      <Route path="/vendor/*" element={<Protected><VendorPortal /></Protected>} />
      <Route path="/super-admin" element={<AdminOnly><SuperAdmin /></AdminOnly>} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  useEffect(() => {
    const TITLE = "Notre Oui — Tout commence par un Oui.";
    document.title = TITLE;
    const id = setInterval(() => { if (document.title !== TITLE) document.title = TITLE; }, 1000);
    return () => clearInterval(id);
  }, []);
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
        <CookieConsent />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}
